import { Analytics } from '@cobliteam/analytics';
import { normalizeVehicleSize, parseVehicleSize, type Vehicle, type VehicleSize } from '@cobliteam/common-types';
import useFuscaVehicle from '@cobliteam/hooks/src/useEntity/useFuscaVehicles';
import useVehicle from '@cobliteam/hooks/src/useEntity/useVehicle';
import { useTranslation } from '@cobliteam/i18n';
import Tag from '@cobliteam/ui/components/Tag';
import Typography from '@cobliteam/ui/components/Typography';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, useForm } from '@cobliteam/ui/form';
import Input from '@cobliteam/ui/form/Input';
import Icon from '@cobliteam/ui/icons';
import { DialogSetupGuideShell } from '@cobliteam/ui-business/components/dialogs/DialogSetupGuideShell/DialogSetupGuideShell';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { z } from 'zod';
import type { SetupGuideEntityFormProps } from '../entityModalConfig';
import SetupGuideFormRow from '../SetupGuideFormRow';
import { runSequentialSaves } from './sequentialSubmit';
import { DEFAULT_KMH, MAX_SPEED_LIMIT, PORTE_ORDER } from './speedDefaults';

/**
 * "Set allowed speed by porte" modal — one row per vehicle porte present on the fleet, each applied
 * to every vehicle of that porte via the `updateVehiclesSpeed` batch mutation. Vehicles without a
 * defined porte are left out (per-road/OSM tuning stays on the dedicated speed page).
 */
const SpeedForm = ({ fleetId, item, onClose, onSuccess }: SetupGuideEntityFormProps) => {
  const { t } = useTranslation('basic-value-onboarding');
  const { data: vehicles } = useVehicle().getVehicles({});
  const { mutateAsync: updateVehiclesSpeed, isPending } = useFuscaVehicle().updateVehiclesSpeed;

  // Group the fleet by porte (size); portes with no vehicle are not shown. The app-layer
  // `Vehicle.size` carries the pt-BR label ("Pequeno"…), so normalize before bucketing.
  const groups = useMemo(() => {
    const bySize = new Map<VehicleSize, Vehicle[]>();
    for (const vehicle of vehicles ?? []) {
      const size = normalizeVehicleSize(vehicle.size);
      if (!size) continue;
      const list = bySize.get(size) ?? [];
      list.push(vehicle);
      bySize.set(size, list);
    }
    return PORTE_ORDER.filter((size) => bySize.has(size)).map((size) => ({ size, vehicles: bySize.get(size) ?? [] }));
  }, [vehicles]);

  const schema = useMemo(
    () =>
      z.object({
        limits: z.record(z.string()).superRefine((limits, ctx) => {
          for (const group of groups) {
            const limit = Number(limits[group.size]);
            if (!Number.isFinite(limit) || limit <= 0 || limit > MAX_SPEED_LIMIT) {
              ctx.addIssue({ code: z.ZodIssueCode.custom, path: [group.size], message: t('entity_modal.speed.validation.speed_range', { max: MAX_SPEED_LIMIT }) });
            }
          }
        }),
      }),
    [groups, t]
  );

  type FormValues = z.infer<typeof schema>;
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { limits: Object.fromEntries(PORTE_ORDER.map((size) => [size, String(DEFAULT_KMH[size])])) },
  });

  const handleSubmit = async ({ limits }: FormValues) => {
    // One batch per porte; a failed porte doesn't stop the others and the hook toasts each failure.
    const { succeeded, failed } = await runSequentialSaves(groups, (group) => updateVehiclesSpeed({ vehicles: group.vehicles, speedLimitInKmh: Number(limits[group.size]) }));
    // Applied portes are saved even when another fails, so the event reflects reality.
    if (succeeded.length > 0) Analytics.track('BasicValueModalItemCreated', { fleetId, feature: 'speed', count: succeeded.length });
    // On any failure, keep the form open for retry — re-applying an already saved porte is harmless.
    if (failed.length > 0) return;
    onSuccess(succeeded.length);
  };

  return (
    <DialogSetupGuideShell
      open
      onClose={onClose}
      title={t('entity_modal.speed.title')}
      description={t('entity_modal.speed.description')}
      primaryAction={{
        label: t('entity_modal.speed.submit'),
        onClick: form.handleSubmit(handleSubmit),
        // The defaults are already valid, so the button starts enabled; RHF only computes
        // `isValid` after the first change, hence the isDirty guard.
        disabled: groups.length === 0 || (form.formState.isDirty && !form.formState.isValid),
        loading: isPending,
        startIcon: <Icon name='check' size='sm' />,
        'data-testid': 'setup-guide-speed-submit',
      }}
    >
      <Form form={form} onSubmit={handleSubmit}>
        <div className='flex flex-col'>
          <Tag color='default' size='sm' label={t('entity_modal.speed.progress', { current: item.current, target: item.target })} />
          {groups.length === 0 ? (
            <Typography variant='text-md' className='py-8 text-center text-gray-11'>
              {t('inline.speed_no_porte')}
            </Typography>
          ) : (
            groups.map((group) => (
              <SetupGuideFormRow key={group.size} label={parseVehicleSize(group.size)}>
                <FormField control={form.control} name={`limits.${group.size}`}>
                  {({ field }) => (
                    <FormItem className='w-64'>
                      <FormLabel>{t('entity_modal.speed.limit_label')}</FormLabel>
                      <FormControl>
                        <Input {...field} inputMode='numeric' placeholder={t('entity_modal.speed.limit_placeholder')} endIcon={<span className='text-gray-9 text-xs'>km/h</span>} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                </FormField>
                <Typography variant='text-sm' className='mt-9 text-gray-9'>
                  {t('inline.speed_porte_count', { count: group.vehicles.length })}
                </Typography>
              </SetupGuideFormRow>
            ))
          )}
        </div>
      </Form>
    </DialogSetupGuideShell>
  );
};

export default SpeedForm;
